import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { COUNTRIES, countryByCode, splitE164, toE164, validatePhone, digitsOnly, ID_TYPES, ID_PLACEHOLDER, formatGovId, validateGovId } from "@/lib/validate";
import { inputCls, labelCls } from "@/components/Kit";

/**
 * One phone component for the whole HMS: searchable country code + country-aware validation.
 * `value` is stored/emitted in E.164 form (e.g. +919876543210).
 */
export function PhoneInput({ label, value, onChange, required = false, testid = "phone",
  hint = "", error = "" }) {
  const parsed = useMemo(() => splitE164(value), [value]);
  const [picked, setPicked] = useState(parsed.country.code);
  const country = value ? parsed.country : countryByCode(picked);
  const national = parsed.national;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [touched, setTouched] = useState(false);

  const list = COUNTRIES.filter((c) => {
    const q = query.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase() === q;
  });

  const emit = (nextCountry, nextNational) => onChange(toE164(nextCountry, nextNational));
  const localError = error || (touched ? validatePhone(country, national, { required, label: label || "Mobile number" }) : "");

  return (
    <div className="block">
      {label && <span className={labelCls}>{label}{required && " *"}</span>}
      <div className="mt-2 flex gap-2">
        <div className="relative shrink-0">
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-country-btn`}
            className="flex h-full items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 hover:border-slate-900">
            <span className="font-mono-num">{country.dial}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>
          {open && (
            <div className="absolute z-50 mt-2 w-72 rounded-lg border border-slate-200 bg-white shadow-xl"
              data-testid={`${testid}-country-list`}>
              <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                <Search className="h-3.5 w-3.5 text-slate-400" />
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                  data-testid={`${testid}-country-search`} placeholder="Search country or code"
                  className="w-full text-sm outline-none" />
              </div>
              <div className="max-h-60 overflow-y-auto py-1">
                {list.map((c) => (
                  <button type="button" key={c.code} data-testid={`${testid}-country-${c.code}`}
                    onClick={() => { setPicked(c.code); emit(c, national); setOpen(false); setQuery(""); }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50">
                    <span className="text-slate-700">{c.name}</span>
                    <span className="flex items-center gap-2 font-mono-num text-xs text-slate-500">
                      {c.dial}{c.code === country.code && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                    </span>
                  </button>
                ))}
                {list.length === 0 && <p className="px-3 py-3 text-xs text-slate-400">No match.</p>}
              </div>
            </div>
          )}
        </div>
        <input inputMode="numeric" className={inputCls} value={national} data-testid={`${testid}-input`}
          onBlur={() => setTouched(true)} placeholder={`${country.len?.[0] || 10}-digit number`}
          onChange={(e) => emit(country, digitsOnly(e.target.value).slice(0, 15))} />
      </div>
      {localError
        ? <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`${testid}-error`}>{localError}</p>
        : hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/** Government ID type + ID-specific number field with live formatting. */
export function GovIdInput({ type, number, country = "IN", onChange, error = "",
  testid = "govid", required = false }) {
  const [touched, setTouched] = useState(false);
  const localError = error || (touched ? validateGovId(type, number, country) : "");
  return (
    <>
      <label className="block">
        <span className={labelCls}>Government ID type{required && " *"}</span>
        <select className={`mt-2 ${inputCls}`} value={type} data-testid={`${testid}-type-select`}
          onChange={(e) => onChange({ type: e.target.value, number: "", country })}>
          {ID_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </label>
      <label className="block">
        <span className={labelCls}>ID number{type && " *"}</span>
        <input className={`mt-2 ${inputCls}`} value={number} data-testid={`${testid}-number-input`}
          disabled={!type} onBlur={() => setTouched(true)}
          placeholder={ID_PLACEHOLDER[type] || "Select an ID type first"}
          onChange={(e) => onChange({ type, number: formatGovId(type, e.target.value), country })} />
        {localError && <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`${testid}-error`}>{localError}</p>}
      </label>
      {(type === "passport" || type === "driving_licence") && (
        <label className="block">
          <span className={labelCls}>Issuing country *</span>
          <select className={`mt-2 ${inputCls}`} value={country} data-testid={`${testid}-country-select`}
            onChange={(e) => onChange({ type, number, country: e.target.value })}>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
      )}
    </>
  );
}
